// ============================================================================
// LE FOND COMMUN DES PAGES, TIRE DE LA PORTE DE L'ECOSYSTEME (29/09/2026).
// ----------------------------------------------------------------------------
// La demande du gerant : « meubler les fonds des pages pour les styliser et les
// rendre incroyables, comme la page de connexion ». La connexion tient sa force de
// trois choses : une lumiere de scene sur un noir profond, un grain tres fin, et le
// V de Vyndra en prisme, aux couleurs des univers, dont les lignes de lumiere
// s'ouvrent sur la page. Ce fond reprend EXACTEMENT ces trois choses, et rien
// d'autre : memes couleurs, meme geometrie (les lignes viennent du prisme lui-meme).
//
// DEUX VARIANTES.
//   - « scene » : les pages hors application (nouveau mot de passe, invitation,
//     compte gele, erreurs, pages publiques). Le V en filigrane geant deborde dans
//     un coin, ses lumieres colorent la scene, ses lignes l'eclairent.
//   - « discret » : l'interieur de l'application. Deux lumieres a peine visibles et
//     le grain : le fond ne doit jamais gener la lecture d'un ecran de chiffres.
//
// LEGER POUR LA MACHINE, EXIGENCE DU GERANT (son PC a sature cette semaine, et il y
// a les telephones) : aucun canvas, aucune animation continue, aucun filtre de flou.
// Les lueurs sont des degrades radiaux, rendus une fois. Seule une apparition d'une
// seconde a l'ouverture, coupee par prefers-reduced-motion.
//
// Purement decoratif : hors de l'arbre lu par un lecteur d'ecran, transparent aux
// clics, et derriere tout (z-index negatif). La page qui l'utilise ne doit donc pas
// peindre elle-meme un fond opaque par-dessus.
// ============================================================================

'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import { VyndraLogo } from '@/components/vyndra-logo';
import { estHoteCandidatEspace } from '@/lib/hote-espace';
import { LIGNES } from './geometrie-prisme';
import s from './fond-ecosysteme.module.css';

export type VarianteFond = 'scene' | 'discret';

export function FondEcosysteme({
  variante = 'scene',
  losanges,
  accent,
  position = 'fixe',
  selonHote = false,
}: {
  variante?: VarianteFond;
  /**
   * Couleur de chacun des six losanges des ailes (colorerPrisme, comme la
   * connexion). Absentes : le V garde les couleurs du logo Vyndra.
   */
  losanges?: string[] | null;
  /** La couleur de l'univers quand la page en porte un : la scene et le V s'y teintent. */
  accent?: string | null;
  /** « fixe » : collé à l'écran pendant le défilement. « absolue » : dans son conteneur. */
  position?: 'fixe' | 'absolue';
  /**
   * Pour une page qui ne connait pas l'univers (la page d'erreur, cote navigateur) :
   * sur l'adresse d'un univers, le V passe au NEUTRE, jamais au violet de Vyndra.
   * Il reste cache tant que l'adresse n'est pas lue : aucun eclair violet.
   */
  selonHote?: boolean;
}) {
  const [hote, setHote] = useState<'vyndra' | 'univers' | null>(selonHote ? null : 'vyndra');
  useEffect(() => {
    if (selonHote) setHote(estHoteCandidatEspace(window.location.host) ? 'univers' : 'vyndra');
  }, [selonHote]);

  // Sur l'adresse d'un univers, le V prend SA couleur : jamais le violet de Vyndra
  // dans un univers (regle du 09/08/2026, scripts/verifier-violet.mjs). La clef de
  // voute garde son or, celui du groupe.
  const peinture = (losanges ?? []).some(Boolean)
    ? losanges!
    : accent
      ? Array.from({ length: 6 }, () => accent)
      : [];
  const variables: Record<string, string> = {};
  peinture.forEach((couleur, i) => {
    if (couleur) variables[`--p${i}`] = couleur;
  });
  if (accent) variables['--lum-accent'] = accent;
  const peint = peinture.some(Boolean);

  return (
    <div
      aria-hidden="true"
      className={`${s.fond} ${position === 'fixe' ? s.fixe : s.absolu}`}
      data-variante={variante}
      data-accent={accent ? '' : undefined}
      data-neutre={hote === 'univers' && !accent && !peint ? '' : undefined}
      style={variables as CSSProperties}
    >
      <div className={s.scene} />
      <div className={s.lumieres} />
      {variante === 'scene' && hote !== null && (
        <div className={s.prisme} data-peint={peint ? '' : undefined}>
          <svg className={s.rayons} viewBox="-700 -700 1600 1600" preserveAspectRatio="xMidYMid meet">
            {LIGNES.map((l, i) => (
              <line
                key={i}
                x1={l.x1}
                y1={l.y1}
                x2={l.x2}
                y2={l.y2}
                className={s.rayon}
                style={{ stroke: `var(--p${l.rang}, var(${l.rang <= 2 ? '--aile-g' : l.rang <= 5 ? '--aile-d' : '--clef'}))` } as CSSProperties}
              />
            ))}
          </svg>
          <div className={s.halo} />
          <VyndraLogo size={200} className={s.filigrane} />
        </div>
      )}
      <div className={s.grain} />
    </div>
  );
}
